import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { AdminLayout } from '../components/layouts/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { 
    Users, UserCheck, UserX, Clock, Calendar, 
    Trash2, Shield, Mail, Activity 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminUsersPage = () => {
    const { language } = useLanguage();
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const getAuthConfig = () => {
        const token = localStorage.getItem('nusatrade_token');
        return { headers: { 'Authorization': `Bearer ${token}` } };
    };

    const fetchData = async () => {
        try {
            const [usersRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/users`, getAuthConfig()),
                axios.get(`${API}/admin/users/stats`, getAuthConfig())
            ]);
            setUsers(usersRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (userId, currentStatus) => {
        try {
            await axios.put(
                `${API}/admin/users/${userId}/status?is_active=${!currentStatus}`,
                {},
                getAuthConfig()
            );
            toast.success(
                currentStatus 
                    ? (language === 'id' ? 'User diblokir!' : 'User blocked!')
                    : (language === 'id' ? 'User diaktifkan!' : 'User activated!')
            );
            fetchData();
        } catch (error) {
            toast.error('Error updating user status');
        }
    };

    const handleDelete = async (userId, userName) => {
        if (!confirm(language === 'id' 
            ? `Yakin hapus user "${userName}"? Semua data progress akan hilang.` 
            : `Delete user "${userName}"? All progress data will be lost.`
        )) return;
        
        try {
            await axios.delete(`${API}/admin/users/${userId}`, getAuthConfig());
            toast.success(language === 'id' ? 'User dihapus!' : 'User deleted!');
            fetchData();
        } catch (error) {
            toast.error('Error deleting user');
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return '-';
        return new Date(isoString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short', year: 'numeric'
        });
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return 'Never';
        return new Date(isoString).toLocaleDateString('id-ID', {
            day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
        });
    };

    if (loading) {
        return (
            <AdminLayout>
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="space-y-6">
                {/* Header */}
                <div>
                    <h1 className="text-2xl font-heading font-bold text-white">
                        {language === 'id' ? 'Kelola Users' : 'Manage Users'}
                    </h1>
                    <p className="text-slate-400 mt-1">
                        {language === 'id' ? 'Pantau dan kelola client yang terdaftar' : 'Monitor and manage registered clients'}
                    </p>
                </div>

                {/* Stats Cards */}
                {stats && (
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-slate-700 flex items-center justify-center">
                                        <Users className="w-5 h-5 text-slate-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-white">{stats.users.total}</p>
                                        <p className="text-xs text-slate-400">Total Users</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                                        <UserCheck className="w-5 h-5 text-emerald-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-emerald-400">{stats.users.active}</p>
                                        <p className="text-xs text-slate-400">Active</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                                        <UserX className="w-5 h-5 text-red-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-red-400">{stats.users.blocked}</p>
                                        <p className="text-xs text-slate-400">Blocked</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-blue-400">{stats.users.recent_7_days}</p>
                                        <p className="text-xs text-slate-400">New (7d)</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-800 border-slate-700">
                            <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
                                        <Activity className="w-5 h-5 text-amber-400" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold text-amber-400">{stats.users.active_today}</p>
                                        <p className="text-xs text-slate-400">Active Today</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                )}

                {/* Users List */}
                <Card className="bg-slate-800 border-slate-700">
                    <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            {language === 'id' ? 'Daftar Client' : 'Client List'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {users.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                                <p className="text-slate-400">
                                    {language === 'id' ? 'Belum ada user' : 'No users yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {users.map((user) => (
                                    <div 
                                        key={user.id}
                                        className={`flex items-center justify-between p-4 rounded-lg border ${
                                            user.is_active !== false 
                                                ? 'bg-slate-700/50 border-slate-600' 
                                                : 'bg-red-900/20 border-red-800/50'
                                        }`}
                                        data-testid={`user-${user.id}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 rounded-full flex items-center justify-center text-lg font-bold ${
                                                user.is_active !== false
                                                    ? 'bg-primary/20 text-primary'
                                                    : 'bg-red-500/20 text-red-400'
                                            }`}>
                                                {user.name?.charAt(0)?.toUpperCase() || 'U'}
                                            </div>
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="font-medium text-white">{user.name}</span>
                                                    {user.is_active === false && (
                                                        <Badge className="bg-red-500/20 text-red-400 border-red-500/30">
                                                            Blocked
                                                        </Badge>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <Mail className="w-3 h-3" />
                                                        {user.email}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Calendar className="w-3 h-3" />
                                                        Joined: {formatDate(user.created_at)}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        Last login: {formatDateTime(user.last_login)}
                                                    </span>
                                                </div>
                                                {user.invitation_code && (
                                                    <div className="mt-1 text-xs text-slate-500">
                                                        <span className="flex items-center gap-1">
                                                            <Shield className="w-3 h-3" />
                                                            Invitation: {user.invitation_code}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.broker && (
                                                    <div className="mt-1">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-amber-500/20 text-amber-400 text-xs">
                                                            Broker: {user.broker}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex items-center gap-2">
                                                <span className="text-xs text-slate-400">
                                                    {user.is_active !== false ? 'Active' : 'Blocked'}
                                                </span>
                                                <Switch
                                                    checked={user.is_active !== false}
                                                    onCheckedChange={() => handleToggleStatus(user.id, user.is_active !== false)}
                                                    data-testid={`toggle-${user.id}`}
                                                />
                                            </div>
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => handleDelete(user.id, user.name)}
                                                className="text-slate-400 hover:text-red-400"
                                                data-testid={`delete-${user.id}`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </AdminLayout>
    );
};
